import { PortalRoutes } from '@app/app-routing.module';
import {
  AncillaryTypeEnum,
  AncillaryUpsellNode,
  PortalAncillaryBundle,
  PortalOrderNode,
} from '@app/services/api/api.types';
import {
  checkoutExtrasFromState,
  PortalSignInRedirectExtras,
} from '@app/services/session-proceed-coordinator.service';
import { State } from '@app/services/state/state.service';

/**
 * Disruption step already saved DP in state; the order exposes both DP + CFAR inside
 * `ancillaryBundle`. After CFAR Continue we collapse into `state.bundle` — sign-in URL should
 * request the same (`bundle=1`) instead of separate DP/CFAR query params.
 */
export function orderBundleIncludesCancelForAnyReason(
  portalOrder: PortalOrderNode | null,
): boolean {
  return (
    portalOrder?.ancillaryBundle?.ancillaries.some(
      (a) => a.type === AncillaryTypeEnum.CANCEL_FOR_ANY_REASON,
    ) ?? false
  );
}

export function buildDisruptionSignInRedirectExtras(params: {
  state: State;
  portalOrder: PortalOrderNode | null;
  disruptionPrice: number;
  ancillaryUpsell: boolean;
  upsell: AncillaryUpsellNode | null;
  upsellTripPortalLink: string | undefined;
}): PortalSignInRedirectExtras {
  const base = checkoutExtrasFromState(params.state) ?? {};
  const hasCfar = orderBundleIncludesCancelForAnyReason(params.portalOrder);

  const core: PortalSignInRedirectExtras = {
    ...base,
    ...(params.disruptionPrice > 0 ? { disruptionProtection: params.disruptionPrice } : {}),
  };

  if (params.ancillaryUpsell && params.upsell) {
    return {
      ...core,
      checkoutResume: hasCfar
        ? PortalRoutes.CancelForAnyReason
        : params.upsell.showGratuity
          ? PortalRoutes.Gratuity
          : PortalRoutes.OverviewPayment,
      checkoutPath: 'upsell',
      upsellTripPortalLink: params.upsellTripPortalLink,
    };
  }

  return {
    ...core,
    checkoutResume: hasCfar ? PortalRoutes.CancelForAnyReason : PortalRoutes.Travelers,
    checkoutPath: 'option',
  };
}

export function hasSequentialDisruptionPlusCfarBundle(
  state: State,
  portalOrder: PortalOrderNode | null,
): boolean {
  const hasDisruptionInState = state.disruptionProtection != null && state.disruptionProtection > 0;
  const ancillaries = portalOrder?.ancillaryBundle?.ancillaries;

  if (!hasDisruptionInState || !ancillaries?.length) {
    return false;
  }

  const types = new Set(ancillaries.map((a) => a.type));

  return (
    types.has(AncillaryTypeEnum.DISRUPTION_PROTECTION) &&
    types.has(AncillaryTypeEnum.CANCEL_FOR_ANY_REASON)
  );
}

export function buildCfarSignInRedirectExtras(params: {
  state: State;
  portalOrder: PortalOrderNode | null;
  cfarPrice: number;
  ancillaryUpsell: boolean;
  upsell: AncillaryUpsellNode | null;
  upsellTripPortalLink: string | undefined;
}): PortalSignInRedirectExtras {
  const base = checkoutExtrasFromState(params.state) ?? {};
  const bundleOnly = hasSequentialDisruptionPlusCfarBundle(params.state, params.portalOrder);

  const core: PortalSignInRedirectExtras = bundleOnly
    ? { tier: base.tier, restoreBundleSelection: true }
    : {
        ...base,
        ...(params.cfarPrice > 0 ? { cancelForAnyReason: params.cfarPrice } : {}),
      };

  if (params.ancillaryUpsell && params.upsell) {
    return {
      ...core,
      checkoutResume: params.upsell.showGratuity
        ? PortalRoutes.Gratuity
        : PortalRoutes.OverviewPayment,
      checkoutPath: 'upsell',
      upsellTripPortalLink: params.upsellTripPortalLink,
    };
  }

  return {
    ...core,
    checkoutResume: PortalRoutes.Travelers,
    checkoutPath: 'option',
  };
}

export type AncillaryBundleSignInUiSnapshot = {
  bundleCardSelected: boolean;
  disruptionProtection?: number;
  cancelForAnyReason?: number;
  bundle?: PortalAncillaryBundle;
};

/**
 * `state.bundle` is only written on Continue — mirror UI selection for the sign-in redirect.
 * When the Bundle card is selected, only tier + `bundle=1` are sent (standalone DP/CFAR omitted).
 */
export function buildAncillaryBundleSignInRedirectExtras(
  state: State,
  ui: AncillaryBundleSignInUiSnapshot,
): PortalSignInRedirectExtras {
  const base = checkoutExtrasFromState(state) ?? {};

  if (ui.bundleCardSelected) {
    return {
      tier: base.tier,
      restoreBundleSelection: true,
    };
  }

  return {
    ...base,
    ...(ui.disruptionProtection != null && ui.disruptionProtection > 0
      ? { disruptionProtection: ui.disruptionProtection }
      : {}),
    ...(ui.cancelForAnyReason != null && ui.cancelForAnyReason > 0
      ? { cancelForAnyReason: ui.cancelForAnyReason }
      : {}),
    ...(ui.bundle ? { restoreBundleSelection: true } : {}),
  };
}
